import { saveCustomer, saveSOWUsersAndImport, showErrorAlert } from 'actions';
import { changePageState, cleanImportState } from 'actions/import';
import { resetForm, upsertIntegrationSettings } from 'actions/integrationSettings';
import LoadingIndicator from 'components/LoadingIndicator';
import moment from 'moment';
import React from 'react';
import { useDispatch } from 'react-redux';
import { getCustomerDomain, getUserCustomer } from 'selectors/auth';
import {
  getCustomerContractsLoading,
  getUpsertCustomerContractLoading,
} from 'selectors/customerContracts';
import {
  getImportEndDate,
  getImportPlatforms,
  getImportScanFor,
  getImportStartDate,
  getImportType,
  getImportUsersListFileName,
} from 'selectors/import';
import { getIntegrationSettingsForm } from 'selectors/integrationSettings';
import { getNavParams } from 'selectors/nav';
import getUsers, { getUsersGeneralCounts } from 'selectors/userPermissionsPage';
import { useSelector } from 'store';
import { useHistory } from 'utils/urls';

type ComponentProps = {
  pageState: number;
};

const ImportHeader: React.FC<ComponentProps> = ({ pageState }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const customer = useSelector(getUserCustomer);
  const customerDomain = useSelector(getCustomerDomain);

  const fetchLoading = useSelector(getCustomerContractsLoading);
  const upsertLoading = useSelector(getUpsertCustomerContractLoading);

  const type = useSelector(getImportType);
  const startDate = useSelector(getImportStartDate);
  const endDate = useSelector(getImportEndDate);
  const platforms = useSelector(getImportPlatforms);
  const scanFor = useSelector(getImportScanFor);
  const fileName = useSelector(getImportUsersListFileName);

  const users = useSelector(getUsers);
  const { insight, prevent } = useSelector(getUsersGeneralCounts);

  const integrationSettings = useSelector(getIntegrationSettingsForm);

  const { only_integration_settings: onlyIntegrationSettings } = useSelector(getNavParams) as {
    only_integration_settings: string;
  };

  const handleImportDetailsValidation = (): boolean => {
    if (!startDate) {
      dispatch(showErrorAlert('Missing Start Date'));
      return false;
    }
    if (type === 'lookback' && !endDate) {
      dispatch(showErrorAlert('Missing End Date'));
      return false;
    }
    if (platforms.length === 0) {
      dispatch(showErrorAlert('Select at least one platform'));
      return false;
    }
    if (!scanFor) {
      dispatch(showErrorAlert('Select attachments platform'));
      return false;
    }
    return true;
  };

  const handleIntegrationSettingsValidation = (): boolean => {
    if (!integrationSettings.legal_hold && integrationSettings.legal_hold !== false) {
      dispatch(showErrorAlert('Missing Legal Hold'));
      return false;
    }
    if (!integrationSettings.email_chat_retention_policy) {
      dispatch(showErrorAlert('Missing Retention Policy'));
      return false;
    }
    if (!integrationSettings.retention_period) {
      dispatch(showErrorAlert('Missing Retention Period'));
      return false;
    }
    if (
      !integrationSettings.translations_needed &&
      integrationSettings.translations_needed !== false
    ) {
      dispatch(showErrorAlert('Missing Translations'));
      return false;
    }
    if (
      !integrationSettings.additional_language_offerings &&
      integrationSettings.additional_language_offerings !== false
    ) {
      dispatch(showErrorAlert('Missing Prevent Additional Language Offerings'));
      return false;
    }

    return true;
  };

  const handleImport = (): void => {
    // save SOW, save users, import
    dispatch(
      saveSOWUsersAndImport({
        users,
        sow: {
          start_date: moment(startDate).format(),
          end_date: endDate ? moment(endDate).format() : undefined,
          contract_filename: '',
          insight_seats: insight || 0,
          prevent_seats: prevent || 0,
          users_list_filename: fileName,
          platforms,
          scan_for: scanFor,
        },
      })
    );
  };

  const handleSaveIntegrationSettings = (): void => {
    if (customer) {
      dispatch(upsertIntegrationSettings(integrationSettings));
      dispatch(
        saveCustomer({
          ...customer,
          config: {
            ...customer.config,
            communication_retention_days: Number.parseInt(integrationSettings.retention_period, 10),
          },
        })
      );
    }
  };

  const handleCancel = (): void => {
    switch (pageState) {
      case 1:
        // go to settings
        dispatch(cleanImportState());
        history.pushLookup({ routeName: 'customer', customerDomain });
        break;
      case 2:
        // back to import details
        dispatch(changePageState(1));
        break;
      case 3:
        dispatch(resetForm());
        if (onlyIntegrationSettings === 'true') {
          // back to accoutn overview
          history.pushLookup({ routeName: 'customer-contracts', customerDomain });
        } else {
          // back to import review
          dispatch(changePageState(2));
        }
        break;
      default:
        break;
    }
  };

  const handleNext = (): void => {
    switch (pageState) {
      case 1:
        if (handleImportDetailsValidation()) {
          // go to import review
          dispatch(changePageState(2));
        }
        break;
      case 2:
        // do import
        handleImport();
        break;
      case 3:
        if (handleIntegrationSettingsValidation()) {
          // save integration options
          handleSaveIntegrationSettings();

          if (onlyIntegrationSettings === 'true') {
            // back to accoutn overview
            history.pushLookup({ routeName: 'customer-contracts', customerDomain });
          } else {
            dispatch(changePageState(2));
          }
        }

        break;
      default:
        break;
    }
  };

  return (
    <div className="w-3/5 pr-10 flex flex-row gap-4 justify-between">
      <div className="flex flex-row items-center">
        <h1 className="text-heading-1 whitespace-no-wrap">
          {pageState === 3 ? 'Edit Integration Details' : 'Import Data'}
        </h1>
        {(upsertLoading || fetchLoading) && <LoadingIndicator className="ml-2" size="5" />}
      </div>

      <div className="flex flex-row gap-2">
        <button
          onClick={handleCancel}
          type="button"
          className="button button--secondary h-8 w-25 justify-center"
          data-testid="new-contract-button"
        >
          <span className="font-bold">
            {pageState === 2 || pageState === 3 ? 'Back' : 'Cancel'}
          </span>
        </button>
        <button
          onClick={handleNext}
          type="button"
          className="button button--primary h-8 w-25 justify-center"
          data-testid="new-contract-button"
        >
          {pageState === 1 && 'Next'}
          {pageState === 2 && 'Import'}
          {pageState === 3 && 'Save'}
        </button>
      </div>
    </div>
  );
};

export default ImportHeader;
